import * as React from "react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "./context-menu";
import { MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface FieldContextMenuProps {
  children: React.ReactNode;
  fieldLabel: string;
  fieldKey: string;
  onQuote: () => void;
  disabled?: boolean;
}

/**
 * FieldContextMenu Component
 *
 * Provides a right-click context menu for form fields to quote them in chat.
 * Features:
 * - Visual indicator on hover (message icon)
 * - Right-click menu with "Quote this field in chat" option
 * - Disabled when not in run mode (review mode)
 *
 * @param children - The form field component to wrap
 * @param fieldLabel - Human-readable label of the field
 * @param fieldKey - Unique key/ID of the field
 * @param onQuote - Callback when user selects "Quote this field"
 * @param disabled - Disable the context menu (e.g., in review mode)
 */

export function FieldContextMenu({
  children,
  fieldLabel,
  fieldKey,
  onQuote,
  disabled = false,
}: FieldContextMenuProps) {
  const { t } = useTranslation();

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="relative group">
          {children}
          {/* Visual indicator on hover */}
          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-primary/80 text-white rounded-full p-1 shadow-lg">
              <MessageCircle className="h-3 w-3" />
            </div>
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-64">
        <ContextMenuItem onClick={onQuote} className="cursor-pointer">
          <MessageCircle className="mr-2 h-4 w-4" />
          <div className="flex flex-col">
            <span className="font-medium">
              {t("quote_field_in_chat") || "Quote this field in chat"}
            </span>
            <span className="text-xs text-muted-foreground">
              {fieldLabel}
            </span>
          </div>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
